/**
 * Firebase Token Utility
 * 
 * Centralized utility for getting Firebase ID tokens with proper refresh handling.
 * This ensures tokens are always fresh and handles expiration gracefully.
 */

import { getAuth } from 'firebase/auth';
import { firebaseApp } from '@/services/firebase/firebase.config';

const auth = getAuth(firebaseApp);

/**
 * Get Firebase ID token for API authentication
 * 
 * This function ensures tokens are fresh by forcing a refresh when needed.
 * In production, tokens can expire and cause 401 errors, so we:
 * 1. Try to get a cached token first (for performance)
 * 2. If that fails or in production, force a refresh to ensure freshness
 * 3. Handle errors gracefully
 * 
 * @param forceRefresh - If true, always force a token refresh (slower but ensures freshness)
 * @returns Promise<string> - The Firebase ID token
 * @throws Error if user is not authenticated
 */
export async function getFirebaseIdToken(forceRefresh: boolean = false): Promise<string> {
  const user = auth.currentUser;
  
  if (!user) {
    throw new Error('User not authenticated');
  }
  
  try {
    // In production, always force refresh to prevent 401 errors from expired tokens
    // In development, use cached token for faster iteration
    const isProduction = import.meta.env.PROD;
    const shouldForceRefresh = forceRefresh || isProduction;
    
    if (shouldForceRefresh) {
      // Force refresh to ensure token is always fresh
      return await user.getIdToken(true);
    }
    
    // In development, try cached token first (faster)
    try {
      return await user.getIdToken(false);
    } catch (error) {
      // If cached token fails, force refresh
      console.warn('[FirebaseToken] Cached token failed, forcing refresh:', error);
      return await user.getIdToken(true);
    }
  } catch (error) {
    // If getting token fails, try forcing a refresh one more time
    console.warn('[FirebaseToken] Failed to get token, attempting final refresh:', error);
    try {
      return await user.getIdToken(true);
    } catch (refreshError) {
      console.error('[FirebaseToken] Token refresh failed:', refreshError);
      throw new Error('Failed to get authentication token. Please sign in again.');
    }
  }
}

/**
 * Get Firebase ID token with automatic retry on 401 errors
 * 
 * This is a wrapper that:
 * 1. Gets a token
 * 2. If the API call fails with 401, forces a refresh and retries
 * 
 * @param apiCall - Function that makes an API call with the token
 * @returns Promise<T> - The result of the API call
 */
export async function getFirebaseIdTokenWithRetry<T>(
  apiCall: (token: string) => Promise<T>
): Promise<T> {
  try {
    // First attempt with cached/fresh token
    const token = await getFirebaseIdToken(false);
    return await apiCall(token);
  } catch (error: unknown) {
    // If we get a 401, the token might be expired - force refresh and retry
    const errorMessage = error instanceof Error ? error.message : String(error);
    const errorStatus = (error as { status?: number })?.status;
    if (errorStatus === 401 || errorMessage?.includes('401') || errorMessage?.includes('unauthorized')) {
      console.warn('[FirebaseToken] Got 401 error, refreshing token and retrying...');
      try {
        const freshToken = await getFirebaseIdToken(true);
        return await apiCall(freshToken);
      } catch (retryError) {
        console.error('[FirebaseToken] Retry after token refresh failed:', retryError);
        throw retryError;
      }
    }
    // If it's not a 401, rethrow the original error
    throw error;
  }
}
